import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  TextField,
  Typography,
  Tabs,
  Tab,
  Button,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchSeoContent,
  saveSeoContent,
} from "../../../redux/slices/orgAdmin/seoSlice";
import { fetchLanguages } from "../../../redux/slices/superAdmin/languagesSlice";
import { StyledContainer } from "../../CSS/Styles";
import Banner from "../../Utils/Snackbar";

export default function SEO() {
  const dispatch = useDispatch();
  const { seoContent, loading } = useSelector((state) => state.seo);
  const { languagesList } = useSelector((state) => state.languages);
  const { companyId } = useSelector((state) => state.app);
  const [currentLanguage, setCurrentLanguage] = useState("en");
  const [seoData, setSeoData] = useState({});
  const [allSeoData, setAllSeoData] = useState({});
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  useEffect(() => {
    dispatch(fetchLanguages({ companyId })).then(() => {});
    dispatch(fetchSeoContent(companyId));
  }, [dispatch, companyId]);

  useEffect(() => {
    if (seoContent?.length) {
      const parsedData = seoContent.reduce((acc, item) => {
        const content = JSON.parse(item?.content || "{}");
        acc[item?.language_code] = {
          title: content?.title || "",
          tag: content?.tag || "",
          description: content?.description || "",
        };
        return acc;
      }, {});
      setSeoData(parsedData);
    }
  }, [seoContent]);

  const handleInputChange = (field, value) => {
    setSeoData((prevData) => ({
      ...prevData,
      [currentLanguage]: {
        ...prevData[currentLanguage],
        [field]: value,
      },
    }));
  };

  const handleSave = () => {
    // Ensure all languages are included in the payload
    const seoPayload = {
      en: seoData?.en || { title: "", tag: "", description: "" },
      fr: seoData?.fr || { title: "", tag: "", description: "" },
      ar: seoData?.ar || { title: "", tag: "", description: "" },
    };

    // Update the data for the current language
    const newData = {
      id: languagesList.find((lang) => lang.code === currentLanguage)?.id,
      title: seoData[currentLanguage]?.title || "",
      tag: seoData[currentLanguage]?.tag || "",
      description: seoData[currentLanguage]?.description || "",
    };

    // Merge current data with the full payload for all languages
    const updatedData = { ...seoPayload, [currentLanguage]: newData };
    debugger;

    // Submit the data if it's the Arabic language or if all languages have been filled
    if (
      currentLanguage === "ar" ||
      currentLanguage === "en" ||
      Object.keys(seoPayload).every((key) => seoPayload[key].title !== "")
    ) {
      submitAllData(updatedData);
    }

    // Move to the next language or stay on the current one
    const languageCodes = languagesList?.map((lang) => lang.code);
    const currentIndex = languageCodes.indexOf(currentLanguage);

    if (currentIndex < languageCodes.length - 1) {
      setCurrentLanguage(languageCodes[currentIndex + 1]);
    }
  };

  const submitAllData = (allData) => {
    const fullPayload = {
      type: "SEO",
      data: allData,
    };

    dispatch(saveSeoContent({ allSeoData: fullPayload }))
      .then(() => {
        dispatch(fetchSeoContent(companyId));
        setSnackbarMessage("SEO successfully saved!");
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
        setCurrentLanguage("en");
      })
      .catch((error) => {
        console.error("❌ Error saving all SEO content:", error);
      });
  };
  return (
    <>
      <StyledContainer>
        <Paper elevation={3} sx={{ padding: 3 }}>
          <Typography variant="h6" gutterBottom>
            SEO Configuration
          </Typography>

          <Tabs
            value={currentLanguage}
            indicatorColor="primary"
            textColor="primary"
            onChange={(e, newValue) => setCurrentLanguage(newValue)}
          >
            {languagesList?.map((lang) => (
              <Tab key={lang.code} value={lang.code} label={lang.name} />
            ))}
          </Tabs>

          <Box sx={{ mt: 3 }}>
            <TextField
              sx={{ mb: 2 }}
              label="Title"
              value={seoData[currentLanguage]?.title || ""}
              fullWidth
              onChange={(e) => handleInputChange("title", e.target.value)}
              inputProps={{ maxLength: 255 }}
              helperText={`${(seoData[currentLanguage]?.title || "").length}/255`}
            />
            <TextField
              sx={{ mb: 2 }}
              label="Tag"
              value={seoData[currentLanguage]?.tag || ""}
              fullWidth
              onChange={(e) => handleInputChange("tag", e.target.value)}
            />
            <TextField
              label="Description"
              multiline
              rows={4}
              fullWidth
              value={seoData[currentLanguage]?.description || ""}
              onChange={(e) => handleInputChange("description", e.target.value)}
              inputProps={{ maxLength: 255 }}
              helperText={`${(seoData[currentLanguage]?.description || "").length}/255`}
            />
          </Box>

          <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
            <Button color="primary" variant="contained" onClick={handleSave}>
              {currentLanguage === languagesList[languagesList.length - 1]?.code
                ? "Save"
                : "Next"}
            </Button>
          </Box>
        </Paper>
      </StyledContainer>
      <Banner
        open={snackbarOpen}
        message={snackbarMessage}
        severity={snackbarSeverity}
        handleClose={() => setSnackbarOpen(false)}
      />
    </>
  );
}
